import { transpileBlock } from "./scratchTranspiler.js";
import { getDeclarations, sortTopLevelBlockKeys } from "./astEvaluator.js";

/**
 * Converts Scratch 3.0 project JSON into clean, readable student JavaScript code.
 * Follows standard Sprite semantics:
 * - Empty workspace: const sprite1 = new Sprite('Sprite1');
 * - Loose blocks: sprite1.move(10);
 * - Event hats: async function whenStart() { ... } sprite1.whenStart(whenStart); ... whenStart();
 */
export const convertScratchToJs = (projectData, targetName = null) => {
  try {
    let projectObj = projectData;
    if (typeof projectData === "string") {
      if (!projectData.trim()) {
        projectObj = null;
      } else {
        try {
          projectObj = JSON.parse(projectData);
        } catch (e) {
          projectObj = null;
        }
      }
    }

    if (!projectObj || !projectObj.targets || projectObj.targets.length === 0) {
      return "const sprite1 = new Sprite('Sprite1');\n";
    }

    const allUsedMethods = new Set();
    const startCallables = [];
    const eventCounters = { flag: 0, key: 0, click: 0, broadcast: 0, backdrop: 0 };

    const stageTarget = projectObj.targets.find((t) => t.isStage);
    const seenSpriteNames = new Set();
    const spriteTargets = projectObj.targets.filter((t) => {
      if (t.isStage || t.isOriginal === false) return false;
      const nameKey = (t.name || "Sprite1").toLowerCase();
      if (seenSpriteNames.has(nameKey)) return false;
      seenSpriteNames.add(nameKey);
      return true;
    });

    // Single target isolation
    if (targetName && targetName !== "_all_") {
      const isTargetStage = targetName.toLowerCase() === "stage" || targetName === "_stage_";

      if (isTargetStage) {
        const outputLines = ["const stage = new Stage();"];
        const declLines = getDeclarations(stageTarget, stageTarget, "javascript");
        if (declLines.length > 0) {
          outputLines.push("", ...declLines);
        }
        outputLines.push("");
        const stageTopKeys = stageTarget && stageTarget.blocks
          ? sortTopLevelBlockKeys(stageTarget.blocks)
          : [];

        if (stageTopKeys.length > 0) {
          stageTopKeys.forEach((topKey) => {
            const ctx = {
              targetLang: "javascript",
              varName: "stage",
              indentLevel: 0,
              baseIndent: 0,
              lines: outputLines,
              usedMethods: allUsedMethods,
              startCallables,
              eventCounters,
            };
            transpileBlock(topKey, stageTarget.blocks, ctx);
            outputLines.push("");
          });
        }

        const cleanedLines = [];
        for (let i = 0; i < outputLines.length; i++) {
          const line = outputLines[i];
          if (line.trim() === "" && cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === "") {
            continue;
          }
          cleanedLines.push(line);
        }

        const joined = cleanedLines.join("\n");
        const helperLines = [];
        if (joined.includes("delay(") || joined.includes("await delay")) {
          helperLines.push("const delay = ms => new Promise(res => setTimeout(res, ms));");
        }

        const finalLines = [];
        if (helperLines.length > 0) {
          finalLines.push(...helperLines);
          finalLines.push("");
        }
        finalLines.push(...cleanedLines);

        if (startCallables.length > 0) {
          while (finalLines.length > 0 && finalLines[finalLines.length - 1].trim() === "") finalLines.pop();
          finalLines.push("");
          startCallables.forEach((fn) => finalLines.push(`${fn}();`));
        }

        return finalLines.join("\n").trim() + "\n";
      } else {
        const target = spriteTargets.find(
          (t) => t.name === targetName || (t.name && t.name.toLowerCase() === targetName.toLowerCase())
        ) || spriteTargets[0];

        if (!target) return "const sprite1 = new Sprite('Sprite1');\n";

        const spriteName = target.name || "Sprite1";
        const varName = spriteName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || "sprite1";
        const outputLines = [`const ${varName} = new Sprite('${spriteName}');`];
        const declLines = getDeclarations(target, stageTarget, "javascript");
        if (declLines.length > 0) {
          outputLines.push("", ...declLines);
        }

        const topBlockKeys = target.blocks
          ? sortTopLevelBlockKeys(target.blocks)
          : [];

        if (topBlockKeys.length > 0) {
          outputLines.push("");
          topBlockKeys.forEach((topKey) => {
            const ctx = {
              targetLang: "javascript",
              varName,
              indentLevel: 0,
              baseIndent: 0,
              lines: outputLines,
              usedMethods: allUsedMethods,
              startCallables,
              eventCounters,
            };
            transpileBlock(topKey, target.blocks, ctx);
            outputLines.push("");
          });
        }

        const cleanedLines = [];
        for (let i = 0; i < outputLines.length; i++) {
          const line = outputLines[i];
          if (line.trim() === "" && cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === "") {
            continue;
          }
          cleanedLines.push(line);
        }

        const joined = cleanedLines.join("\n");
        const helperLines = [];
        if (joined.includes("delay(") || joined.includes("await delay")) {
          helperLines.push("const delay = ms => new Promise(res => setTimeout(res, ms));");
        }

        const finalLines = [];
        if (helperLines.length > 0) {
          finalLines.push(...helperLines);
          finalLines.push("");
        }
        finalLines.push(...cleanedLines);

        if (startCallables.length > 0) {
          while (finalLines.length > 0 && finalLines[finalLines.length - 1].trim() === "") finalLines.pop();
          finalLines.push("");
          startCallables.forEach((fn) => finalLines.push(`${fn}();`));
        }

        return finalLines.join("\n").trim() + "\n";
      }
    }

    // If no sprite exists, fallback
    if (spriteTargets.length === 0 && (!stageTarget || !stageTarget.blocks || Object.keys(stageTarget.blocks).length === 0)) {
      return "const sprite1 = new Sprite('Sprite1');\n";
    }

    const outputLines = [];
    let hasAnyBlocks = false;

    // Check if stage has top-level blocks
    const stageTopKeys = stageTarget && stageTarget.blocks
      ? sortTopLevelBlockKeys(stageTarget.blocks)
      : [];

    if (stageTopKeys.length > 0) {
      hasAnyBlocks = true;
      outputLines.push("const stage = new Stage();");
      const stageDeclLines = getDeclarations(stageTarget, stageTarget, "javascript");
      if (stageDeclLines.length > 0) {
        outputLines.push("", ...stageDeclLines);
      }
      outputLines.push("");
      stageTopKeys.forEach((topKey) => {
        const ctx = {
          targetLang: "javascript",
          varName: "stage",
          indentLevel: 0,
          baseIndent: 0,
          lines: outputLines,
          usedMethods: allUsedMethods,
          startCallables,
          eventCounters,
        };
        transpileBlock(topKey, stageTarget.blocks, ctx);
        outputLines.push("");
      });
    }

    // Process sprite targets
    spriteTargets.forEach((target, idx) => {
      const spriteName = target.name || `Sprite${idx + 1}`;
      const varName = spriteName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || `sprite${idx + 1}`;

      const topBlockKeys = target.blocks
        ? sortTopLevelBlockKeys(target.blocks)
        : [];

      outputLines.push(`const ${varName} = new Sprite('${spriteName}');`);
      const declLines = getDeclarations(target, stageTarget, "javascript");
      if (declLines.length > 0) {
        outputLines.push("", ...declLines);
      }

      if (topBlockKeys.length > 0) {
        hasAnyBlocks = true;
        outputLines.push("");
        topBlockKeys.forEach((topKey) => {
          const ctx = {
            targetLang: "javascript",
            varName,
            indentLevel: 0,
            baseIndent: 0,
            lines: outputLines,
            usedMethods: allUsedMethods,
            startCallables,
            eventCounters,
          };
          transpileBlock(topKey, target.blocks, ctx);
          outputLines.push("");
        });
      }
    });

    // If no blocks across any targets, return clean sprite instantiation
    if (!hasAnyBlocks) {
      const declLines = spriteTargets.length > 0 ? getDeclarations(spriteTargets[0], stageTarget, "javascript") : [];
      const sName = (spriteTargets[0] && spriteTargets[0].name) || "Sprite1";
      const vName = sName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || "sprite1";
      if (declLines.length > 0) {
        return `const ${vName} = new Sprite('${sName}');\n\n${declLines.join("\n")}\n`;
      }
      if (spriteTargets.length <= 1) {
        return `const ${vName} = new Sprite('${sName}');\n`;
      }
      return (
        spriteTargets
          .map((t, idx) => {
            const sName = t.name || `Sprite${idx + 1}`;
            const vName = sName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || `sprite${idx + 1}`;
            return `const ${vName} = new Sprite('${sName}');`;
          })
          .join("\n") + "\n"
      );
    }

    // Clean up consecutive blank lines
    const cleanedLines = [];
    for (let i = 0; i < outputLines.length; i++) {
      const line = outputLines[i];
      if (line.trim() === "" && cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === "") {
        continue;
      }
      cleanedLines.push(line);
    }

    // Check if delay helper is needed
    const joined = cleanedLines.join("\n");
    const helperLines = [];
    if (joined.includes("delay(") || joined.includes("await delay")) {
      helperLines.push("const delay = ms => new Promise(res => setTimeout(res, ms));");
    }

    const finalLines = [];
    if (helperLines.length > 0) {
      finalLines.push(...helperLines);
      finalLines.push("");
    }
    finalLines.push(...cleanedLines);

    // Call start event functions at the bottom for inline execution
    if (startCallables.length > 0) {
      while (finalLines.length > 0 && finalLines[finalLines.length - 1].trim() === "") {
        finalLines.pop();
      }
      finalLines.push("");
      startCallables.forEach((fn) => {
        finalLines.push(`${fn}();`);
      });
    }

    return finalLines.join("\n").trim() + "\n";
  } catch (err) {
    return `// Error transpiling Scratch to JS: ${err.message}`;
  }
};

/**
 * Converts Scratch 3.0 project JSON into a multi-file JavaScript structure:
 * - Stage.js
 * - <SpriteName>.js for each sprite
 */
export const convertScratchToJsMultiFile = (projectData) => {
  try {
    let projectObj = projectData;
    if (typeof projectData === "string") {
      if (!projectData.trim()) projectObj = null;
      else {
        try { projectObj = JSON.parse(projectData); } catch (e) { projectObj = null; }
      }
    }

    if (!projectObj || !projectObj.targets || projectObj.targets.length === 0) {
      return {
        files: [
          {
            id: "stage",
            name: "Stage.js",
            targetName: "Stage",
            isStage: true,
            content: "const stage = new Stage();\n",
          },
          {
            id: "sprite1",
            name: "Sprite1.js",
            targetName: "Sprite1",
            isStage: false,
            content: "const sprite1 = new Sprite('Sprite1');\n",
          },
        ],
        activeTarget: "Sprite1",
      };
    }

    const stageTarget = projectObj.targets.find((t) => t.isStage);
    const seenNames = new Set();
    const spriteTargets = projectObj.targets.filter((t) => {
      if (t.isStage || t.isOriginal === false) return false;
      const nameKey = (t.name || "Sprite1").toLowerCase();
      if (seenNames.has(nameKey)) return false;
      seenNames.add(nameKey);
      return true;
    });
    const files = [];

    // 1. Stage.js
    const stageContent = convertScratchToJs(projectObj, "Stage");
    files.push({
      id: stageTarget ? stageTarget.id : "stage",
      name: "Stage.js",
      targetName: stageTarget ? stageTarget.name : "Stage",
      isStage: true,
      content: stageContent,
    });

    // 2. Sprite files
    spriteTargets.forEach((target, idx) => {
      const spriteName = target.name || `Sprite${idx + 1}`;
      const safeName = spriteName.replace(/[^a-zA-Z0-9_-]/g, "_") || `Sprite${idx + 1}`;
      const fileName = `${safeName}.js`;
      const content = convertScratchToJs(projectObj, spriteName);

      files.push({
        id: target.id || `sprite_${idx + 1}`,
        name: fileName,
        targetName: spriteName,
        isStage: false,
        content: content,
      });
    });

    const activeTarget = spriteTargets.length > 0 ? spriteTargets[0].name : "Stage";

    return { files, activeTarget };
  } catch (err) {
    return {
      files: [{ id: "main", name: "main.js", targetName: "Main", isStage: false, content: `// Error: ${err.message}` }],
      activeTarget: "Main",
    };
  }
};

/**
 * Generates a self-contained, 100% offline-runnable JavaScript file for downloads.
 */
export const getStandaloneJsCode = (code) => {
  return `// =======================================================
// Practido JavaScript Standalone Engine (Offline Runner)
// =======================================================
const delay = ms => new Promise(res => setTimeout(res, ms));

class PractidoSprite {
    constructor(name) {
        this.name = name;
        this.x = 0;
        this.y = 0;
        this.size = 100;
        this.visible = true;
        this.costume = { name: 'costume1', index: 1 };
        this.direction = 90;
        this.volume = 100;
        this.layer = 1;
        this.rotationStyle = 'all around';
        this.dragMode = 'draggable';
        this.effects = {};
        this._eventHandlers = {};
    }
    move(steps) { console.log(\`[\${this.name}] Moved \${steps} steps forward 🐾\`); }
    turnRight(deg) { this.direction = (this.direction + deg) % 360; console.log(\`[\${this.name}] Turned right \${deg}° 🔄\`); }
    turnLeft(deg) { this.direction = (this.direction - deg) % 360; console.log(\`[\${this.name}] Turned left \${deg}° 🔄\`); }
    goTo(x, y) { this.x = x; this.y = y; console.log(\`[\${this.name}] Went to x:\${x}, y:\${y} 📍\`); }
    setX(x) { this.x = x; console.log(\`[\${this.name}] Set X to \${x}\`); }
    setY(y) { this.y = y; console.log(\`[\${this.name}] Set Y to \${y}\`); }
    changeX(dx) { this.x += dx; console.log(\`[\${this.name}] Changed X by \${dx} (X: \${this.x})\`); }
    changeY(dy) { this.y += dy; console.log(\`[\${this.name}] Changed Y by \${dy} (Y: \${this.y})\`); }
    pointInDirection(dir) { this.direction = dir; console.log(\`[\${this.name}] Pointed in direction \${dir}°\`); }
    pointTowards(target) { console.log(\`[\${this.name}] Pointed towards \${target} 🎯\`); }
    bounceIfOnEdge() { console.log(\`[\${this.name}] Bounced on edge 💥\`); }
    setRotationStyle(style) { this.rotationStyle = style; }
    async glide(secs, x, y) { console.log(\`[\${this.name}] Glided to x:\${x}, y:\${y} 🚀\`); await delay(secs * 1000); }
    async glideTo(secs, target) { console.log(\`[\${this.name}] Glided to \${target} 🚀\`); await delay(secs * 1000); }
    say(msg) { console.log(\`[\${this.name}] Says: \${msg} 💬\`); }
    think(msg) { console.log(\`[\${this.name}] Thinks: \${msg} 💭\`); }
    show() { this.visible = true; console.log(\`[\${this.name}] Shown 👁️\`); }
    hide() { this.visible = false; console.log(\`[\${this.name}] Hidden 🙈\`); }
    switchCostume(name) { this.costume.name = name; console.log(\`[\${this.name}] Switched costume to \${name} 🎭\`); }
    nextCostume() { this.costume.index++; console.log(\`[\${this.name}] Next costume 🎭\`); }
    setSize(s) { this.size = Math.max(5, s); console.log(\`[\${this.name}] Set size to \${this.size}%\`); }
    changeSize(c) { this.size = Math.max(5, this.size + c); console.log(\`[\${this.name}] Changed size by \${c}% (Size: \${this.size}%)\`); }
    setEffect(eff, val) { this.effects[eff] = val; }
    changeEffect(eff, chg) { this.effects[eff] = (this.effects[eff] || 0) + chg; }
    clearGraphicEffects() { this.effects = {}; }
    goToLayer(layer) { this.layer = layer === 'front' ? 1 : 0; }
    changeLayer(dir, num) { this.layer += dir === 'forward' ? num : -num; }
    playSound(sound) { console.log(\`[\${this.name}] Played sound: \${sound} 🔊\`); }
    async playSoundUntilDone(sound) { console.log(\`[\${this.name}] Played sound: \${sound} 🔊\`); await delay(1000); }
    setVolume(v) { this.volume = Math.max(0, Math.min(100, v)); }
    changeVolume(v) { this.volume = Math.max(0, Math.min(100, this.volume + v)); }
    isTouching(target) { return false; }
    isTouchingColor(color) { return false; }
    colorIsTouchingColor(c1, c2) { return false; }
    distanceTo(target) { return 0; }
    setDragMode(mode) { this.dragMode = mode; }
    whenStart(fn) { this._eventHandlers['start'] = fn; }
    whenKeyPressed(key, fn) { this._eventHandlers['key_' + key] = fn; }
    whenClicked(fn) { this._eventHandlers['click'] = fn; }
    whenBroadcastReceived(msg, fn) { this._eventHandlers['broadcast_' + msg] = fn; }
    whenGreaterThan(elem, val, fn) { this._eventHandlers['gt_' + elem] = fn; }
    whenStartAsClone(fn) { this._eventHandlers['clone'] = fn; }
    createClone(target = 'myself') {
        console.log(\`[\${this.name}] Created clone of \${target}\`);
        if (typeof this._eventHandlers['clone'] === 'function') {
            this._eventHandlers['clone']();
        }
    }
    deleteThisClone() { console.log(\`[\${this.name}] Deleted clone\`); }
    deleteClone() { console.log(\`[\${this.name}] Deleted clone\`); }
}

class PractidoStage {
    constructor() {
        this.backdrop = { name: 'backdrop1', index: 1 };
        this._eventHandlers = {};
    }
    switchBackdrop(name) { console.log(\`[Stage] Switched backdrop to \${name} 🏞️\`); }
    async switchBackdropAndWait(name) { console.log(\`[Stage] Switched backdrop to \${name} 🏞️\`); await delay(500); }
    nextBackdrop() { console.log('[Stage] Next backdrop 🌆'); }
    whenBackdropSwitch(name, fn) { this._eventHandlers['backdrop_' + name] = fn; }
}

const Sprite = PractidoSprite;
const Stage = PractidoStage;

const sound = {
    volume: 100,
    changeEffect: (eff, val) => {},
    setEffect: (eff, val) => {},
    clearEffects: () => {},
};

const keyboard = {
    isPressed: key => false,
    on: (key, handler) => { if (typeof handler === 'function') handler(); },
};

const mouse = { x: 0, y: 0, isDown: false };
let timerStart = Date.now();
let user_answer = '';

function onBroadcast(msg, handler) { if (typeof handler === 'function') handler(); }
function onBackdropSwitch(bd, handler) { if (typeof handler === 'function') handler(); }
function whenGreaterThan(elem, val, handler) { if (typeof handler === 'function') handler(); }
function onStartAsClone(handler) { if (typeof handler === 'function') handler(); }
function deleteThisClone() {}
function stopAllSounds() { console.log('Stopped all sounds 🔇'); }
function broadcast(msg) { console.log(\`Broadcasted message: \${msg} 📢\`); }
async function broadcastAndWait(msg) { console.log(\`Broadcasted message: \${msg} 📢\`); await delay(500); }

// =======================================================
// 🚀 Student Script
// =======================================================
(async () => {
${code}
})();
`;
};

